import { css, html, nothing } from 'lit';
import { api } from '../../lib/api.js';
import { authorDraft, authorLabel, authorPayload, freelancerLabel } from '../../lib/authors.js';
import { BaseComponent } from '../../lib/base-component.js';

/** @type {Promise<import('../../lib/authors.js').Freelancer[]> | null} */
let freelancersLoad = null;

/**
 * The freelancers (and staff) an author can be, loaded once per page: the list
 * comes from d3-auth and is slow. A failed load is forgotten so the next editor
 * opened tries again.
 */
function loadFreelancers() {
    if (!freelancersLoad) {
        freelancersLoad = api
            .get('/api/freelancers?include_staff=true')
            .then((data) => data.freelancers || [])
            .catch((err) => {
                freelancersLoad = null;
                throw err;
            });
    }
    return freelancersLoad;
}

/**
 * Who made one version of an asset's art (issue #215): the credit, linked when
 * an outside author has a link, and a pencil that opens an inline form to set
 * it to one of our freelancers or an outside name and link, or to clear it.
 * Saving fires `author-save` with the PUT body ({user_id} | {name, url?} | {}
 * to clear); the page calls the API and passes the reloaded author back in.
 */
class AuthorEditor extends BaseComponent {
    static properties = {
        author: { type: Object },
        busy: { type: Boolean },
        _open: { type: Boolean, state: true },
        _draft: { type: Object, state: true },
        _freelancers: { type: Array, state: true },
        _error: { type: String, state: true },
    };

    static styles = css`
        :host { display: block; font-size: 0.85rem; }
        .credit { display: inline-flex; align-items: center; gap: 0.35rem; }
        .credit .btn { font-size: 0.75rem; padding: 0 0.25rem; line-height: 1.2; }
        form { display: grid; gap: 0.35rem; max-width: 18rem; }
        form .form-select, form .form-control { font-size: 0.8rem; padding: 0.15rem 0.4rem; }
        form .btn { font-size: 0.75rem; padding: 0.1rem 0.45rem; }
        .kind { display: flex; gap: 0.75rem; }
        .kind label { display: inline-flex; align-items: center; gap: 0.25rem; }
    `;

    constructor() {
        super();
        this.author = null;
        this.busy = false;
        this._open = false;
        this._draft = authorDraft(null);
        this._freelancers = null;
        this._error = '';
    }

    updated(changed) {
        // A save answered: the page passed the new author in, so the form closes.
        if (changed.has('author') && changed.get('author') !== undefined) this._open = false;
    }

    async _start() {
        this._draft = authorDraft(this.author);
        this._error = '';
        this._open = true;
        if (this._freelancers) return;
        try {
            this._freelancers = await loadFreelancers();
        } catch (err) {
            // Left unloaded, so opening the editor again tries again.
            this._error = `Couldn't load the freelancers: ${err.message}`;
        }
    }

    _set(field, value) {
        this._draft = { ...this._draft, [field]: value };
    }

    _save(e) {
        e.preventDefault();
        const result = authorPayload(this._draft);
        if ('error' in result) {
            this._error = result.error;
            return;
        }
        this._fire(result.body);
    }

    _fire(body) {
        this._error = '';
        this.dispatchEvent(
            new CustomEvent('author-save', { detail: { body }, bubbles: true, composed: true }),
        );
    }

    _renderCredit() {
        const label = authorLabel(this.author);
        const credit = !label
            ? html`<span class="text-muted">Unknown</span>`
            : this.author.url
              ? html`<a href="${this.author.url}" target="_blank" rel="noopener">${label}</a>`
              : html`<span>${label}</span>`;
        return html`
            <span class="credit">
                ${credit}
                <button class="btn btn-link" title="Set the author" aria-label="Set the author" ?disabled="${this.busy}" @click="${this._start}">
                    <i class="bi bi-pencil"></i>
                </button>
            </span>
        `;
    }

    _renderForm() {
        const d = this._draft;
        return html`
            <form @submit="${this._save}">
                <div class="kind">
                    <label><input type="radio" name="kind" .checked="${d.kind === 'freelancer'}" @change="${() => this._set('kind', 'freelancer')}"> Freelancer</label>
                    <label><input type="radio" name="kind" .checked="${d.kind === 'outside'}" @change="${() => this._set('kind', 'outside')}"> Outside</label>
                </div>
                ${
                    d.kind === 'freelancer'
                        ? html`
                    <select class="form-select" aria-label="Freelancer" @change="${(e) => this._set('userId', e.target.value)}">
                        <option value="" ?selected="${!d.userId}">${this._freelancers ? 'Choose a freelancer…' : this._error ? 'Unavailable' : 'Loading…'}</option>
                        ${(this._freelancers || []).map((f) => html`<option value="${f.id}" ?selected="${d.userId === f.id}">${freelancerLabel(f)}</option>`)}
                    </select>
                `
                        : html`
                    <input class="form-control" placeholder="Name, e.g. Forgotten Adventures" aria-label="Author name"
                        .value="${d.name}" @input="${(e) => this._set('name', e.target.value)}">
                    <input class="form-control" placeholder="Link (optional)" aria-label="Author link" type="url"
                        .value="${d.url}" @input="${(e) => this._set('url', e.target.value)}">
                `
                }
                ${this._error ? html`<div class="text-danger small">${this._error}</div>` : nothing}
                <div>
                    <button class="btn btn-primary btn-sm" type="submit" ?disabled="${this.busy}">Save</button>
                    ${this.author ? html`<button class="btn btn-outline-danger btn-sm" type="button" ?disabled="${this.busy}" @click="${() => this._fire({})}">Clear</button>` : nothing}
                    <button class="btn btn-outline-secondary btn-sm" type="button" ?disabled="${this.busy}" @click="${() => (this._open = false)}">Cancel</button>
                </div>
            </form>
        `;
    }

    render() {
        return this._open ? this._renderForm() : this._renderCredit();
    }
}

customElements.define('author-editor', AuthorEditor);
